import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Save, Minus, Plus, PhoneCall, Clock, ArrowRight, MessageCircle, Ban, HelpCircle, RotateCcw, Sun } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Super admin: how many times the Qualifying Agent phones a candidate, how long it waits
 * between calls, and the hours it may call in, for one organisation. Stored as `qualifyingAgent`
 * in the org config and frozen onto each qualifying session at invite time. Limits mirror
 * CALL_POLICY_LIMITS / CALL_WINDOW_BOUNDS / DEFAULT_CALL_HOURS in
 * server/modules/qualifying-agent/callSchedule.js (the server checks them as well).
 */

const DEFAULTS = {
  maxCallAttempts: 3,
  retryDelayMinutes: 240,
  callWindowStart: '10:00',
  callWindowEnd: '21:00',
  channelPriority: 'call_first',
  candidateQuestions: false,
};
// The widest call hours allowed in India, and the shortest window.
const HOURS = { start: '08:00', end: '22:00', minMinutes: 60 };
const ATTEMPTS = { min: 1, max: 5 };
const DELAY = { min: 15, max: 1440 };
const DELAY_PRESETS = [
  { minutes: 15, label: '15 min' },
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
  { minutes: 180, label: '3 hours' },
  { minutes: 240, label: '4 hours' },
  { minutes: 360, label: '6 hours' },
];

const RETRIED = [
  'No answer, line busy, rejected or switched off',
  'Call cut or dropped before every question was answered',
  '“I’m busy” / “call me later”',
];
const NEVER = ['Wrong or unreachable number', '“Don’t call me” or a clear “not interested”', 'Every question answered'];

const toMinutes = (hm) => {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hm || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

function formatClock(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  return `${h % 12 || 12}:${String(totalMinutes % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** The problem with the call hours in words, or '' when they are fine (validateCallWindow on the server). */
function callHoursError(start, end) {
  const s = toMinutes(start);
  const e = toMinutes(end);
  if (s === null || e === null) return 'Enter both times.';
  if (s < toMinutes(HOURS.start) || e > toMinutes(HOURS.end)) return 'Calls are allowed only between 8:00 AM and 10:00 PM.';
  if (e - s < HOURS.minMinutes) return 'Keep at least one hour between the two times.';
  return '';
}

function formatDelay(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} hour${h === 1 ? '' : 's'}`;
}

/**
 * "10:00 AM", "Next day · 10:00 AM": the preview timeline for a candidate who never picks up (or
 * picks up and hangs up before answering), from a first call when the call hours open. Mirrors
 * recallPlan in server/modules/qualifying-agent/callSchedule.js: call 2 after the delay, every
 * later call the next day at the first call's time.
 */
function previewTimes(attempts, delayMinutes, hoursStart, hoursEnd) {
  const open = toMinutes(hoursStart);
  const end = toMinutes(hoursEnd) - 10; // last dial ten minutes before the hours close
  const DAY = 24 * 60;
  const times = [];
  let t = open; // minutes since the first day's midnight
  for (let i = 0; i < attempts; i += 1) {
    if (i === 1) t += delayMinutes;
    else if (i > 1) t = (Math.floor(t / DAY) + 1) * DAY + open;
    // Outside the hours: the next opening (the server adds a per-candidate spread of up to 30 min).
    const mins = t % DAY;
    if (mins < open) t = t - mins + open;
    else if (mins >= end) t = t - mins + DAY + open;
    const day = Math.floor(t / DAY);
    const label = formatClock(t % DAY);
    times.push(day ? `${day === 1 ? 'Next day' : `Day ${day + 1}`} · ${label}` : label);
  }
  return times;
}

export default function QualifyingAgentAdmin({ orgId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(DEFAULTS);
  const [attempts, setAttempts] = useState(DEFAULTS.maxCallAttempts);
  const [delay, setDelay] = useState(DEFAULTS.retryDelayMinutes);
  const [delayText, setDelayText] = useState(String(DEFAULTS.retryDelayMinutes));
  const [hoursStart, setHoursStart] = useState(DEFAULTS.callWindowStart);
  const [hoursEnd, setHoursEnd] = useState(DEFAULTS.callWindowEnd);
  const [priority, setPriority] = useState(DEFAULTS.channelPriority);
  const [candidateQuestions, setCandidateQuestions] = useState(DEFAULTS.candidateQuestions);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/internal/org-config/${orgId}`);
        const qa = res.data?.config?.qualifyingAgent || {};
        const loaded = {
          maxCallAttempts: qa.maxCallAttempts ?? DEFAULTS.maxCallAttempts,
          retryDelayMinutes: qa.retryDelayMinutes ?? DEFAULTS.retryDelayMinutes,
          callWindowStart: qa.callWindowStart || DEFAULTS.callWindowStart,
          callWindowEnd: qa.callWindowEnd || DEFAULTS.callWindowEnd,
          channelPriority: qa.channelPriority === 'whatsapp_first' ? 'whatsapp_first' : 'call_first',
          candidateQuestions: res.data?.config?.features?.qualifying_agent_candidate_questions === true,
        };
        if (cancelled) return;
        setSaved(loaded);
        setAttempts(loaded.maxCallAttempts);
        setDelay(loaded.retryDelayMinutes);
        setDelayText(String(loaded.retryDelayMinutes));
        setHoursStart(loaded.callWindowStart);
        setHoursEnd(loaded.callWindowEnd);
        setPriority(loaded.channelPriority);
        setCandidateQuestions(loaded.candidateQuestions);
      } catch (err) {
        toast.error('Failed to load Qualifying Agent settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const delayValid = Number.isInteger(delay) && delay >= DELAY.min && delay <= DELAY.max;
  const hoursError = callHoursError(hoursStart, hoursEnd);
  const dirty =
    attempts !== saved.maxCallAttempts ||
    delay !== saved.retryDelayMinutes ||
    hoursStart !== saved.callWindowStart ||
    hoursEnd !== saved.callWindowEnd ||
    priority !== saved.channelPriority ||
    candidateQuestions !== saved.candidateQuestions;

  const onDelayText = (text) => {
    setDelayText(text);
    const n = Number(text);
    setDelay(text.trim() && Number.isFinite(n) ? Math.round(n) : NaN);
  };

  const pickDelay = (minutes) => {
    setDelay(minutes);
    setDelayText(String(minutes));
  };

  const isDefault =
    attempts === DEFAULTS.maxCallAttempts &&
    delay === DEFAULTS.retryDelayMinutes &&
    hoursStart === DEFAULTS.callWindowStart &&
    hoursEnd === DEFAULTS.callWindowEnd;

  // The call schedule only: the channel order and candidate questions are separate choices.
  const resetToDefaults = () => {
    setAttempts(DEFAULTS.maxCallAttempts);
    pickDelay(DEFAULTS.retryDelayMinutes);
    setHoursStart(DEFAULTS.callWindowStart);
    setHoursEnd(DEFAULTS.callWindowEnd);
  };

  const handleSave = async () => {
    if (!delayValid) {
      toast.error(`Wait before the second call must be ${DELAY.min}–${DELAY.max} minutes`);
      return;
    }
    if (hoursError) {
      toast.error(`Call hours: ${hoursError}`);
      return;
    }
    try {
      setSaving(true);
      await axios.put(`/api/internal/org-config/${orgId}`, {
        qualifyingAgent: {
          maxCallAttempts: attempts,
          retryDelayMinutes: delay,
          callWindowStart: hoursStart,
          callWindowEnd: hoursEnd,
          channelPriority: priority,
        },
        features: { qualifying_agent_candidate_questions: candidateQuestions },
      });
      setSaved({
        maxCallAttempts: attempts,
        retryDelayMinutes: delay,
        callWindowStart: hoursStart,
        callWindowEnd: hoursEnd,
        channelPriority: priority,
        candidateQuestions,
      });
      toast.success('Qualifying Agent settings saved — they apply to candidates invited from now on');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#007BFF] border-t-transparent" />
      </div>
    );
  }

  const previewHours = hoursError
    ? { start: DEFAULTS.callWindowStart, end: DEFAULTS.callWindowEnd }
    : { start: hoursStart, end: hoursEnd };
  const times = previewTimes(attempts, delayValid ? delay : DEFAULTS.retryDelayMinutes, previewHours.start, previewHours.end);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Qualifying Agent — call retries</h3>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            When a candidate does not pick up, cuts the call, or asks to be called later, the agent
            phones again and continues from the first unanswered question. Calls are only placed
            within the call hours below (10 AM – 9 PM IST by default).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={saving || isDefault}
            onClick={resetToDefaults}
            data-testid="qa-admin-reset"
            title="3 calls · the second 4 hours after the first · the third the next day · 10 AM – 9 PM IST"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to defaults
          </button>
          <button
            type="button"
            disabled={saving || !dirty || !delayValid || Boolean(hoursError)}
            onClick={handleSave}
            className="inline-flex items-center gap-2 rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-medium text-white hover:bg-[#0069d9] transition-colors disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Attempts */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <PhoneCall className="h-4 w-4 text-[#007BFF]" />
            <h4 className="text-sm font-semibold text-gray-900">Calls per candidate</h4>
          </div>
          <p className="mt-1 text-xs text-gray-500">Total calls, including the first one.</p>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              aria-label="Fewer calls"
              disabled={attempts <= ATTEMPTS.min}
              onClick={() => setAttempts((a) => Math.max(ATTEMPTS.min, a - 1))}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="w-12 text-center text-3xl font-bold tabular-nums text-gray-900" data-testid="qa-admin-attempts">
              {attempts}
            </span>
            <button
              type="button"
              aria-label="More calls"
              disabled={attempts >= ATTEMPTS.max}
              onClick={() => setAttempts((a) => Math.min(ATTEMPTS.max, a + 1))}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              <Plus className="h-4 w-4" />
            </button>
            <span className="text-xs text-gray-400">
              {ATTEMPTS.min}–{ATTEMPTS.max} · default {DEFAULTS.maxCallAttempts}
            </span>
          </div>
        </div>

        {/* Delay */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#007BFF]" />
            <h4 className="text-sm font-semibold text-gray-900">Wait before the second call</h4>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            How long after a missed first call the second is placed. Any later call goes the next day, at the
            time of the first. A call picked up and cut before any answer counts as missed.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {DELAY_PRESETS.map((p) => (
              <button
                key={p.minutes}
                type="button"
                onClick={() => pickDelay(p.minutes)}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                  delay === p.minutes
                    ? 'border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF]'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={DELAY.min}
              max={DELAY.max}
              step={5}
              value={delayText}
              onChange={(e) => onDelayText(e.target.value)}
              aria-label="Wait before the second call, in minutes"
              data-testid="qa-admin-delay"
              className={cn(
                'w-24 rounded-lg border px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2',
                delayValid ? 'border-gray-300 focus:ring-[#007BFF]/40' : 'border-red-300 focus:ring-red-200'
              )}
            />
            <span className="text-xs text-gray-500">minutes</span>
            {!delayValid && (
              <span className="text-xs font-medium text-red-600">
                {DELAY.min}–{DELAY.max} minutes
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Call hours */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <Sun className="h-4 w-4 text-[#007BFF]" />
          <h4 className="text-sm font-semibold text-gray-900">Call hours (India time)</h4>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          No call starts outside these hours, or in their last 10 minutes; a call that falls outside moves to
          the next morning. A screening’s own call hours are kept inside these. Between 8 AM and 10 PM, at
          least one hour · default 10 AM – 9 PM.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input
            type="time"
            value={hoursStart}
            onChange={(e) => setHoursStart(e.target.value)}
            aria-label="Call hours start"
            data-testid="qa-admin-hours-start"
            className={cn(
              'rounded-lg border px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2',
              hoursError ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-[#007BFF]/40'
            )}
          />
          <span className="text-xs text-gray-500">to</span>
          <input
            type="time"
            value={hoursEnd}
            onChange={(e) => setHoursEnd(e.target.value)}
            aria-label="Call hours end"
            data-testid="qa-admin-hours-end"
            className={cn(
              'rounded-lg border px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2',
              hoursError ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-[#007BFF]/40'
            )}
          />
          {hoursError ? (
            <span className="text-xs font-medium text-red-600">{hoursError}</span>
          ) : (
            <span className="text-xs text-gray-500">Last call starts at {formatClock(toMinutes(hoursEnd) - 10)}</span>
          )}
        </div>
      </div>

      {/* Channel order */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-[#007BFF]" />
          <h4 className="text-sm font-semibold text-gray-900">Call + WhatsApp screenings start with</h4>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          The first channel the agent tries. If it does not reach the candidate, the agent continues on
          the other from the question they got to. Recruiters do not see this choice.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {[
            { value: 'call_first', label: 'Phone call' },
            { value: 'whatsapp_first', label: 'WhatsApp' },
          ].map((o) => (
            <button
              key={o.value}
              type="button"
              data-testid={`qa-admin-priority-${o.value}`}
              onClick={() => setPriority(o.value)}
              className={cn(
                'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                priority === o.value
                  ? 'border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF]'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* What candidates can ask */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-[#007BFF]" />
          <h4 className="text-sm font-semibold text-gray-900">Answering candidate questions</h4>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          How recruiters tell the agent what it may answer when a candidate asks about the job. Front-line
          hiring needs only a short note; turn on the full step for clients whose candidates ask a lot.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {[
            { value: false, label: 'Additional context box', hint: 'One free-text box on the Questions step' },
            { value: true, label: 'Candidate questions step', hint: 'Ready-made questions, written answers, fallback reply' },
          ].map((o) => (
            <button
              key={String(o.value)}
              type="button"
              data-testid={`qa-admin-candidate-questions-${o.value ? 'step' : 'box'}`}
              onClick={() => setCandidateQuestions(o.value)}
              className={cn(
                'rounded-lg border px-3 py-1.5 text-left transition-colors',
                candidateQuestions === o.value
                  ? 'border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF]'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              )}
            >
              <span className="block text-xs font-semibold">{o.label}</span>
              <span className="block text-[11px] font-normal text-gray-500">{o.hint}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
        <h4 className="text-sm font-semibold text-gray-900">What a candidate who keeps missing the call gets</h4>
        <p className="mt-0.5 text-xs text-gray-500">
          Example with a first call at {formatClock(toMinutes(previewHours.start))}, when the call hours open. A
          call that would land after the hours end moves to the next morning.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {times.map((t, i) => (
            <React.Fragment key={i}>
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-[#007BFF]/20 bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 shadow-sm">
                <PhoneCall className="h-3.5 w-3.5 text-[#007BFF]" />
                Call {i + 1} · {t}
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
            </React.Fragment>
          ))}
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700">
            <MessageCircle className="h-3.5 w-3.5" />
            WhatsApp (Call + WhatsApp screenings)
          </span>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          {attempts === 1
            ? 'One call only — no retries.'
            : `The second call ${formatDelay(delayValid ? delay : DEFAULTS.retryDelayMinutes)} after the first${attempts > 2 ? ', then one call a day at the time of the first' : ''}. A call picked up and cut before any answer counts as missed; one that dropped after they answered is retried within the hour.`}{' '}
          A re-call opens with “We’re calling again from … we spoke a little earlier” and asks only the questions still unanswered.
        </p>
      </div>

      {/* When */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h4 className="text-sm font-semibold text-gray-900">Called again after</h4>
          <ul className="mt-2 space-y-1.5">
            {RETRIED.map((r) => (
              <li key={r} className="flex items-start gap-2 text-xs text-gray-600">
                <PhoneCall className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#007BFF]" />
                {r}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h4 className="text-sm font-semibold text-gray-900">Never called again after</h4>
          <ul className="mt-2 space-y-1.5">
            {NEVER.map((r) => (
              <li key={r} className="flex items-start gap-2 text-xs text-gray-600">
                <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                {r}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="text-[11px] text-gray-400">
        Changes apply to candidates invited after saving. Candidates already being called keep the
        settings they were invited with.
      </p>
    </div>
  );
}
